import Utils from "./utils";
/** @typedef {import('../common/types.d').Notification} Notification */

export const _notificationDrawerState = {
    markingAllAsRead: false,
};

/**
 * Initialize the notification drawer.
 */
async function initNotificationDrawer() {
    const notificationDrawer = document.querySelector("#notification-drawer");
    if (!notificationDrawer) {
        return;
    }
    _notificationDrawerState.markingAllAsRead = false;
    setInterval(async () => {
        _updateUnreadMessages();
    }, 5 * globalThis.MINUTE_MS);
    _updateUnreadMessages();

    window.addEventListener("notification-drawer:refreshed", async (event) => {
        const { source } = event.detail;
        if (source === "notification-drawer") {
            return;
        }
        await _updateUnreadMessages();
    });

    const markAllAsReadButton = document.querySelector("#notification-drawer__mark-all-as-read");
    if (markAllAsReadButton) {
        markAllAsReadButton.addEventListener("click", async (event) => {
            event.preventDefault();
            await _markAllAsRead();
        });
    }
}

/**
 * Mark all notifications as read.
 * @returns {Promise<void>}
 * @private
 * @visibleForTesting
 */
async function _markAllAsRead() {
    if (_notificationDrawerState.markingAllAsRead) {
        console.warn("Already marking all notifications as read");
        return;
    }
    _notificationDrawerState.markingAllAsRead = true;
    const markAllAsReadButton = document.querySelector("#notification-drawer__mark-all-as-read");
    const markAllAsReadButtonSpinner = document.querySelector("#notification-drawer__mark-all-as-read-spinner");
    if (markAllAsReadButton) {
        markAllAsReadButton.classList.add("disabled");
        markAllAsReadButton.setAttribute("aria-disabled", "true");
        markAllAsReadButton.setAttribute("aria-busy", "true");
        markAllAsReadButtonSpinner.classList.remove("d-none");
    }
    const response = await Utils.api("notifications/mark-read", { method: "POST" });
    if (markAllAsReadButton) {
        markAllAsReadButton.classList.remove("disabled");
        markAllAsReadButton.removeAttribute("aria-disabled");
        markAllAsReadButton.removeAttribute("aria-busy");
        markAllAsReadButtonSpinner.classList.add("d-none");
    }
    _notificationDrawerState.markingAllAsRead = false;
    if (response.success) {
        _updateUnreadMessages();
    } else {
        UI.error(`${t("Couldn't mark all notifications as read")} ${response.code}: ${response.error}`);
    }
}

/**
 * Update the unread messages count and notification list.
 * @private
 * @visibleForTesting
 */
async function _updateUnreadMessages() {
    const response = await Utils.api("notifications/dropdown");
    const notificationList = document.querySelector("#notification-drawer__list");
    const notificationListLastChild = document.querySelector("#notification-drawer__actions");
    const unreadNotificationCountElements = document.querySelectorAll(".notification-drawer__unread-count");
    const notificationItems = document.querySelectorAll(".notification-drawer__item");
    notificationItems.forEach((item) => {
        item.remove();
    });
    if (response.success) {
        const unreadNotificationsCount = response.notifications.filter((n) => n.isUnread).length;
        window.dispatchEvent(
            new CustomEvent("notification-drawer:refreshed", {
                detail: {
                    source: "notification-drawer",
                    newestNotificationTimestamp: response.notifications[0]?.timestamp,
                },
            })
        );
        for (const element of unreadNotificationCountElements) {
            element.innerText = unreadNotificationsCount > 9 ? "9+" : unreadNotificationsCount.toString();
            element.classList.toggle("d-none-force", unreadNotificationsCount === 0);
        }
        if (response.notifications.length > 0) {
            for (const notification of response.notifications) {
                const li = _createNotificationElement(notification);
                notificationList.insertBefore(li, notificationListLastChild);
            }
            document.querySelector("#notification-drawer__mark-all-as-read").classList.remove("d-none-force");
        } else {
            const li = document.createElement("li");
            const noNotificationsDiv = document.createElement("div");
            noNotificationsDiv.id = "notification-drawer__empty";
            noNotificationsDiv.classList.add(
                "notification-drawer__no-notifications",
                "notification-drawer__item",
                "p-4"
            );
            // large centered no messages icon
            noNotificationsDiv.innerHTML = `<i class="fas fa-inbox fa-3x" role="presentation"></i><p class="mt-2">${t("No notifications")}</p>`;
            li.appendChild(noNotificationsDiv);
            notificationList.insertBefore(li, notificationListLastChild);
            document.querySelector("#notification-drawer__mark-all-as-read").classList.add("d-none-force");
        }
    } else {
        const li = document.createElement("li");
        const notificationLoadErrorDiv = document.createElement("div");
        notificationLoadErrorDiv.id = "notification-drawer__error";
        notificationLoadErrorDiv.classList.add(
            "notification-drawer__no-notifications",
            "notification-drawer__item",
            "error",
            "p-4"
        );
        // large centered no messages icon
        notificationLoadErrorDiv.innerHTML = `<i class="fas fa-exclamation-circle fa-3x" role="presentation"></i><p class="mt-2">${t("Couldn't load notifications")}</p>`;
        li.appendChild(notificationLoadErrorDiv);
        notificationList.insertBefore(li, notificationListLastChild);
        for (const element of unreadNotificationCountElements) {
            element.innerText = "!"; // error
            element.classList.remove("d-none-force");
        }
        UI.error(`${t("Couldn't load notifications")} ${response.code}: ${response.error}`);
        document.querySelector("#notification-drawer__mark-all-as-read").classList.add("d-none-force");
    }
}

/**
 * Create a notification element.
 * @param {Notification} notification - The notification object.
 * @returns {HTMLLIElement} The created notification list item element.
 * @private
 * @visibleForTesting
 */
function _createNotificationElement(notification) {
    const li = document.createElement("li");
    li.classList.add("menu-item", "notification-drawer__item");

    const notificationClickableWrap = _createNotificationClickableWrap(notification);
    const notificationContainer = _createNotificationContainer(notification);

    notificationClickableWrap.appendChild(notificationContainer);
    li.appendChild(notificationClickableWrap);
    return li;
}

/**
 * Create the clickable wrapper for a notification.
 * @param {Notification} notification - The notification object.
 * @returns {HTMLAnchorElement} The created clickable wrapper element.
 * @private
 * @visibleForTesting
 */
function _createNotificationClickableWrap(notification) {
    const readableTime = Utils.timeSince(notification.timestamp, "s", true, 30, t("Long time ago"));
    const notificationClickableWrap = document.createElement("a");
    notificationClickableWrap.classList.add("notification-drawer__clickable");
    if (notification.url) {
        notificationClickableWrap.href = notification.url;
        if (notification.url.startsWith("http")) {
            notificationClickableWrap.target = "_blank";
        }
    } else {
        notificationClickableWrap.href = "/account#notifications";
    }

    const ariaLabelElements = [
        t("Notification from"),
        notification.display.from,
        readableTime,
        notification.title,
        notification.snippet || notification.content,
        notification.url,
    ].filter(Boolean);

    notificationClickableWrap.setAttribute("aria-label", ariaLabelElements.join(" "));
    notificationClickableWrap.setAttribute("role", "link");
    return notificationClickableWrap;
}

/**
 * Create the container for a notification.
 * @param {Notification} notification - The notification object.
 * @returns {HTMLDivElement} The created container element.
 * @private
 * @visibleForTesting
 */
function _createNotificationContainer(notification) {
    const notificationContainer = document.createElement("div");
    notificationContainer.classList.add("notification-drawer__item", "p-2");
    notificationContainer.setAttribute("aria-hidden", "true");

    const notificationTitleAndSnippet = _createNotificationTitleAndSnippet(notification);
    notificationContainer.appendChild(notificationTitleAndSnippet);

    return notificationContainer;
}

/**
 * Create the title and snippet section for a notification.
 * @param {Notification} notification - The notification object.
 * @returns {HTMLDivElement} The created title and snippet section element.
 * @private
 * @visibleForTesting
 */
function _createNotificationTitleAndSnippet(notification) {
    const notificationTitleAndSnippet = document.createElement("div");
    notificationTitleAndSnippet.classList.add("d-flex", "flex-column");
    notificationTitleAndSnippet.setAttribute("aria-hidden", "true");

    const notificationContainerFromAndDate = _createNotificationFromAndDate(notification);
    notificationTitleAndSnippet.appendChild(notificationContainerFromAndDate);

    const notificationTitleContainer = _createNotificationTitleContainer(notification);
    notificationTitleAndSnippet.appendChild(notificationTitleContainer);

    if (notification.snippet) {
        const notificationSnippet = document.createElement("span");
        notificationSnippet.classList.add("notification-drawer__snippet");
        notificationSnippet.innerText = notification.snippet;
        notificationSnippet.setAttribute("aria-hidden", "true");
        notificationTitleAndSnippet.appendChild(notificationSnippet);
    } else if (notification.content) {
        const notificationContent = document.createElement("span");
        notificationContent.classList.add("notification-drawer__snippet");
        notificationContent.innerText =
            notification.content.length > 100 ? notification.content.slice(0, 97) + "..." : notification.content;
        notificationContent.setAttribute("aria-hidden", "true");
        notificationTitleAndSnippet.appendChild(notificationContent);
    }

    return notificationTitleAndSnippet;
}

/**
 * Create the from and date section for a notification.
 * @param {Notification} notification - The notification object.
 * @returns {HTMLDivElement} The created from and date section element.
 * @private
 * @visibleForTesting
 */
function _createNotificationFromAndDate(notification) {
    const readableTime = Utils.timeSince(notification.timestamp, "s", true, 30, t("Long time ago"));
    const notificationContainerFromAndDate = document.createElement("div");
    notificationContainerFromAndDate.classList.add(
        "d-flex",
        "justify-content-between",
        "align-items-center",
        "notification-drawer__from-date"
    );
    notificationContainerFromAndDate.setAttribute("aria-hidden", "true");

    const notificationFrom = document.createElement("span");
    notificationFrom.classList.add("notification-drawer__from");
    notificationFrom.innerText = notification.display.from;
    if (notification.isSenderVerified) {
        notificationFrom.innerHTML += ` <i class="fas fa-check-circle text-success notification-drawer__verified-icon" title="${t("Verified")}"></i>`;
    }
    notificationFrom.setAttribute("aria-hidden", "true");
    notificationContainerFromAndDate.appendChild(notificationFrom);

    const notificationDate = document.createElement("span");
    notificationDate.classList.add("notification-drawer__date");
    notificationDate.innerText = readableTime;
    notificationDate.setAttribute("aria-hidden", "true");
    notificationContainerFromAndDate.appendChild(notificationDate);

    return notificationContainerFromAndDate;
}

/**
 * Create the title container for a notification.
 * @param {Notification} notification - The notification object.
 * @returns {HTMLDivElement} The created title container element.
 * @private
 * @visibleForTesting
 */
function _createNotificationTitleContainer(notification) {
    const notificationTitleContainer = document.createElement("div");
    notificationTitleContainer.classList.add("ellipsis-span-container");
    notificationTitleContainer.setAttribute("aria-hidden", "true");

    const notificationTitle = document.createElement("span");
    notificationTitle.classList.add("notification-drawer__title");
    notificationTitle.innerText = notification.title;
    notificationTitle.setAttribute("aria-hidden", "true");
    notificationTitleContainer.appendChild(notificationTitle);

    if (notification.isUnread) {
        const notificationNewPill = document.createElement("span");
        notificationNewPill.classList.add("badge", "badge-primary", "mr-2", "badge-pill");
        notificationNewPill.innerText = t("New");
        notificationNewPill.setAttribute("aria-hidden", "true");
        notificationTitleContainer.insertBefore(notificationNewPill, notificationTitleContainer.firstChild);
    }

    if (notification.url) {
        const notificationUrlIcon = document.createElement("i");
        notificationUrlIcon.classList.add("notification-drawer__url-icon");
        if (notification.url.startsWith("http")) {
            notificationUrlIcon.classList.add("fas", "fa-external-link-alt", "ml-2");
        } else {
            notificationUrlIcon.classList.add("fas", "fa-link", "ml-2");
        }
        notificationUrlIcon.setAttribute("aria-hidden", "true");
        notificationTitleContainer.appendChild(notificationUrlIcon);
    }

    return notificationTitleContainer;
}

export default initNotificationDrawer;
export const _testExports = {
    _markAllAsRead,
    _updateUnreadMessages,
    _createNotificationElement,
    _createNotificationClickableWrap,
    _createNotificationContainer,
    _createNotificationTitleAndSnippet,
    _createNotificationFromAndDate,
    _createNotificationTitleContainer,
};
